import React from 'react';
import { X, Calendar, Paperclip, Mail, Filter } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { EmailSearchParams } from '@/services/search';

interface EmailSearchFiltersProps {
  filters: EmailSearchParams;
  onFiltersChange: (filters: Omit<EmailSearchParams, 'query'>) => void;
  onClose: () => void;
}

export const EmailSearchFilters: React.FC<EmailSearchFiltersProps> = ({
  filters,
  onFiltersChange,
  onClose
}) => {
  const handleSearchInChange = (value: EmailSearchParams['searchIn']) => {
    onFiltersChange({ ...filters, searchIn: value });
  };

  const handleDateChange = (field: 'since' | 'before', value: string) => {
    onFiltersChange({ ...filters, [field]: value });
  };

  const handleCheckboxChange = (field: 'hasAttachments' | 'unread', checked: boolean) => {
    onFiltersChange({ ...filters, [field]: checked });
  };

  const handleFolderChange = (folder: string) => {
    const currentFolders = filters.folders || [];
    const newFolders = currentFolders.includes(folder)
      ? currentFolders.filter(f => f !== folder)
      : [...currentFolders, folder];
    onFiltersChange({ ...filters, folders: newFolders });
  };

  const resetFilters = () => {
    onFiltersChange({
      searchIn: 'all',
      hasAttachments: false,
      unread: false,
      folders: [],
      page: 1,
      limit: 20
    });
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-gray-600" />
          <h2 className="font-medium text-gray-900">Search Filters</h2>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X size={16} className="text-gray-600" />
        </button>
      </div>

      {/* Filters Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Search In */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Search In</label>
          <div className="grid grid-cols-2 gap-2">
            {['all', 'subject', 'body', 'from', 'to'].map((option) => (
              <button
                key={option}
                onClick={() => handleSearchInChange(option as EmailSearchParams['searchIn'])}
                className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                  filters.searchIn === option
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                {option.charAt(0).toUpperCase() + option.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Date Range */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Date Range</label>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-gray-500" />
              <input
                type="date"
                value={filters.since || ''}
                onChange={(e) => handleDateChange('since', e.target.value)}
                className="flex-1 px-3 py-2 rounded-md border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-gray-500" />
              <input
                type="date"
                value={filters.before || ''}
                onChange={(e) => handleDateChange('before', e.target.value)}
                className="flex-1 px-3 py-2 rounded-md border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Additional Filters */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">Additional Filters</label>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={filters.hasAttachments || false}
                onChange={(e) => handleCheckboxChange('hasAttachments', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <Paperclip size={16} className="text-gray-500" />
              Has Attachments
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={filters.unread || false}
                onChange={(e) => handleCheckboxChange('unread', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <Mail size={16} className="text-gray-500" />
              Unread Only
            </label>
          </div>
        </div>

        {/* Folders */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Search in Folders</label>
          <div className="space-y-2">
            {['INBOX', 'Sent', 'Drafts', 'Spam', 'Trash'].map((folder) => (
              <label key={folder} className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={filters.folders?.includes(folder) || false}
                  onChange={() => handleFolderChange(folder)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                {folder}
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between gap-4">
          <Button
            variant="outline"
            onClick={resetFilters}
            className="flex-1"
          >
            Reset
          </Button>
          <Button
            variant="default"
            onClick={onClose}
            className="flex-1"
          >
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
}; 